import type { AcademicYear, ActionHistory, ClassCategory, ClassSummary, Gradebook, ImportResult, ImportRow, ScoreColumn, Student, StudentImportResult, StudentImportRow, StudentTransferMapping } from "./types";

type ApiErrorBody = { error?: string };

let writeQueue: Promise<unknown> = Promise.resolve();
let activity = { pending: 0, version: 0, failures: 0 };
const listeners = new Set<() => void>();
function updateActivity(pending: number, completed = false) {
  activity = { ...activity, pending, version: activity.version + (completed ? 1 : 0) };
  listeners.forEach((listener) => listener());
}
export const mutationActivity = {
  subscribe: (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; },
  snapshot: () => activity,
  settled: () => writeQueue,
};

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const writing = Boolean(init?.method && init.method !== "GET" && !["/api/login", "/api/logout"].includes(url));
  if (!writing) return send<T>(url, init);
  updateActivity(activity.pending + 1);
  const result = writeQueue.then(() => send<T>(url, init));
  writeQueue = result.catch(() => { activity = { ...activity, failures: activity.failures + 1 }; })
    .finally(() => updateActivity(activity.pending - 1, true));
  return result;
}

async function send<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    credentials: "include",
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...init?.headers,
    },
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as ApiErrorBody;
    throw new Error(body.error || "Đã có lỗi xảy ra. Vui lòng thử lại.");
  }

  return (await response.json()) as T;
}

export const api = {
  history: () => request<ActionHistory>("/api/history"),
  undo: (token: string) => request<ActionHistory & { label: string }>("/api/history/undo", { method: "POST", body: JSON.stringify({ token }) }),
  redo: (token: string) => request<ActionHistory & { label: string }>("/api/history/redo", { method: "POST", body: JSON.stringify({ token }) }),
  session: () => request<{ authenticated: boolean; username?: string }>("/api/session"),
  login: (username: string, password: string, remember = false) =>
    request<{ authenticated: true; username: string }>("/api/login", {
      method: "POST",
      body: JSON.stringify({ username, password, remember }),
    }),
  logout: () => request<{ ok: true }>("/api/logout", { method: "POST", body: "{}" }),
  academicYears: () => request<{ years: AcademicYear[]; currentYear: string }>("/api/academic-years"),
  classes: (academicYear?: string) => request<{ classes: ClassSummary[] }>(`/api/classes${academicYear ? `?academicYear=${encodeURIComponent(academicYear)}` : ""}`),
  createClass: (name: string, academicYear: string, category: ClassCategory = "regular") =>
    request<{ class: ClassSummary }>("/api/classes", {
      method: "POST",
      body: JSON.stringify({ name, academicYear, category }),
    }),
  updateClass: (classId: string, name: string, category?: ClassCategory) =>
    request<{ class: ClassSummary }>(`/api/classes/${encodeURIComponent(classId)}`, {
      method: "PUT",
      body: JSON.stringify({ name, category }),
    }),
  renameClass: (classId: string, name: string, category?: ClassCategory) =>
    request<{ class: ClassSummary }>(`/api/classes/${encodeURIComponent(classId)}`, {
      method: "PUT",
      body: JSON.stringify({ name, category }),
    }),
  deleteClass: (classId: string) =>
    request<{ ok: true }>(`/api/classes/${encodeURIComponent(classId)}`, {
      method: "DELETE",
      body: "{}",
    }),
  gradebook: (classId: string) =>
    request<Gradebook>(`/api/gradebook?classId=${encodeURIComponent(classId)}`),
  renameScoreColumn: (columnId: string, name: string) =>
    request<{ id: string; label: string; shortLabel: string }>(`/api/score-columns/${encodeURIComponent(columnId)}`, {
      method: "PUT",
      body: JSON.stringify({ name }),
    }),
  configureScoreColumn: (columnId: string, name: string, weight: number, mode: ScoreColumn["mode"]) =>
    request<{ column: ScoreColumn }>(`/api/score-columns/${encodeURIComponent(columnId)}`, {
      method: "PUT",
      body: JSON.stringify({ name, weight, mode }),
    }),
  createScoreColumn: (classId: string, name: string, weight: number, mode: ScoreColumn["mode"]) =>
    request<{ column: ScoreColumn }>("/api/score-columns", { method: "POST", body: JSON.stringify({ classId, name, weight, mode }) }),
  deleteScoreColumn: (columnId: string) =>
    request<{ ok: true }>(`/api/score-columns/${encodeURIComponent(columnId)}`, { method: "DELETE", body: "{}" }),
  reorderScoreColumns: (classId: string, columnIds: string[]) =>
    request<{ ok: true }>("/api/score-columns/order", { method: "PUT", body: JSON.stringify({ classId, columnIds }) }),
  updateScore: (studentId: string, columnId: string, value: number | null, component?: "multipleChoice" | "written") =>
    request<{ ok: true; value: number | null; multipleChoice: number | null; written: number | null }>("/api/scores", {
      method: "PUT",
      body: JSON.stringify({ studentId, columnId, value, component }),
    }),
  importScores: (classId: string, columnId: string, rows: ImportRow[], component?: "multipleChoice" | "written") =>
    request<ImportResult>("/api/import", {
      method: "POST",
      body: JSON.stringify({ classId, columnId, rows, component }),
    }),
  importStudents: (classId: string, rows: StudentImportRow[]) =>
    request<StudentImportResult>("/api/students/import", {
      method: "POST",
      body: JSON.stringify({ classId, rows }),
    }),
  updateStudentStatus: (studentId: string, status: Student["status"]) =>
    request<{ status: Student["status"] }>(`/api/students/${encodeURIComponent(studentId)}/status`, {
      method: "PUT", body: JSON.stringify({ status }),
    }),
  transferStudent: (studentId: string, sourceClassId: string, targetClassId: string, studentCode: string, mappings: StudentTransferMapping[]) =>
    request<{ ok: true; targetClassId: string }>(`/api/students/${encodeURIComponent(studentId)}/transfer`, {
      method: "POST", body: JSON.stringify({ sourceClassId, targetClassId, studentCode, mappings }),
    }),
};
